import {
  FlaskConical,
  Briefcase,
  HardHat,
  Dna,
  Truck,
  Shuffle,
  Siren,
  Eye,
  ShieldCheck,
  Clock,
  MapPin,
  BadgeCheck,
} from "lucide-react";

export const SERVICES = [
  {
    id: "drug-testing",
    icon: FlaskConical,
    title: "Drug Testing",
    short: "5-panel, 10-panel, rapid and lab-backed urine & oral fluid testing.",
    desc: "Comprehensive drug screening options including 5-panel and 10-panel tests, rapid-result screening, and certified laboratory-backed urine and oral fluid collections — tailored for workplace programs, personal needs, and court-ordered requirements.",
  },
  {
    id: "pre-employment",
    icon: Briefcase,
    title: "Pre-Employment & Pre-Screening",
    short: "Candidate screening performed at your HR office or designated site.",
    desc: "Seamless pre-employment drug and health screenings performed directly at your corporate HR office or any designated site, so candidates never have to travel and your hiring timeline stays on track.",
  },
  {
    id: "workers-comp",
    icon: HardHat,
    title: "Workers' Compensation Testing",
    short: "Post-incident collection following official workplace protocols.",
    desc: "Immediate post-incident specimen collection following official workplace and insurance protocols to support claim verification, compliance documentation, and a smooth return-to-work process.",
  },
  {
    id: "dna-testing",
    icon: Dna,
    title: "DNA & Paternity Testing",
    short: "Legal chain-of-custody and informational DNA collection.",
    desc: "Strict chain-of-custody legal paternity, relationship, and informational DNA collections with accredited laboratory processing — handled discreetly at your home, office, or attorney's location.",
  },
  {
    id: "dot-testing",
    icon: Truck,
    title: "DOT Testing",
    short: "FMCSA, FAA & FRA collections under 49 CFR Part 40.",
    desc: "Department of Transportation regulated collections performed by certified collectors in full adherence to 49 CFR Part 40 guidelines — for owner-operators, fleets, and safety-sensitive employees.",
  },
  {
    id: "random-testing",
    icon: Shuffle,
    title: "Random Employee Testing",
    short: "Unannounced on-site selection testing with zero workflow disruption.",
    desc: "Unannounced, on-site random selection testing programs designed to maintain safe, compliant workplaces without pulling employees off the job or disrupting your operations.",
  },
  {
    id: "post-accident",
    icon: Siren,
    title: "Post-Accident Testing",
    short: "24/7 rapid dispatch to the incident site within regulatory windows.",
    desc: "Time-sensitive post-accident testing dispatched directly to the incident site, clinic, or hospital to meet strict regulatory deadlines — available around the clock, including nights, weekends, and holidays.",
  },
  {
    id: "reasonable-suspicion",
    icon: Eye,
    title: "Reasonable Suspicion Testing",
    short: "Discreet, professional on-site collection when impairment is suspected.",
    desc: "Discreet, compassionate, and professional on-site collection when supervisors or managers identify potential workplace impairment — handled respectfully to protect everyone involved.",
  },
];

export const TRUST_BADGES = [
  { icon: BadgeCheck, label: "Certified Collectors" },
  { icon: Truck, label: "DOT Compliant" },
  { icon: ShieldCheck, label: "Confidential & Secure" },
  { icon: Clock, label: "Fast Lab Results" },
  { icon: Siren, label: "24/7 Emergency Response" },
];

export const CITIES = [
  "Cedar Rapids",
  "Iowa City",
  "Marion",
  "Hiawatha",
  "Coralville",
  "North Liberty",
  "Davenport",
  "Bettendorf",
  "Quad Cities",
  "Dubuque",
  "Muscatine",
  "Waterloo",
  "Cedar Falls",
  "Tipton",
  "Anamosa",
  "Maquoketa",
  "Mount Vernon",
  "Vinton",
  "DeWitt",
  "Clinton",
  "Dyersville",
  "Manchester",
  "Independence",
  "Washington",
  "Williamsburg",
  "Monticello",
];

export const ZIP_PREFIXES = ["522", "523", "524", "527", "528", "520", "521", "506", "507"];

export const MOBILE_STEPS = [
  {
    title: "Call, text, or request online",
    desc: "Tell us what testing you need and where you are. For urgent needs we dispatch immediately — day or night.",
  },
  {
    title: "We schedule around you",
    desc: "Pick the time and place that works: your office, job site, facility, or home. Same-day and last-minute slots available.",
  },
  {
    title: "Certified collector arrives on-site",
    desc: "A professional collector arrives with everything needed, verifies identity, and maintains strict privacy throughout.",
  },
  {
    title: "Sealed, documented, delivered",
    desc: "Specimens are sealed under chain-of-custody, sent to certified labs, and results are delivered securely and fast.",
  },
];

export const PILLARS = [
  {
    icon: BadgeCheck,
    title: "Clinical Professionalism",
    desc: "Every collection is performed by trained, certified collectors who treat your team with respect and care.",
  },
  {
    icon: ShieldCheck,
    title: "Uncompromising Chain of Custody",
    desc: "Documented, tamper-evident handling from collection to lab so results stand up to legal and regulatory scrutiny.",
  },
  {
    icon: MapPin,
    title: "Client-First Mobile Flexibility",
    desc: "We built SwiftTest around one idea: the testing should come to you, not the other way around.",
  },
  {
    icon: Eye,
    title: "Discreet & Confidential",
    desc: "Private, judgment-free service with secure handling of personal information at every step.",
  },
];

export const FAQS = [
  {
    category: "General & Mobile Dispatch",
    items: [
      {
        q: "How does mobile testing work?",
        a: "You tell us what testing you need and where you are. A certified collector travels to your office, job site, facility, or home with all required equipment, performs the collection on-site, and handles sealed transport to a certified laboratory. You never visit a clinic.",
      },
      {
        q: "How fast can you get to me?",
        a: "For urgent and post-accident needs we offer 24/7 dispatch across Eastern Iowa and can often be on-site within hours. For planned testing, same-day and next-day appointments are usually available.",
      },
      {
        q: "Do you really come to homes and job sites?",
        a: "Yes. We regularly serve offices, manufacturing floors, construction sites, hospitals, attorneys' offices, and private residences. Anywhere within our Eastern Iowa service area works.",
      },
      {
        q: "Is there a walk-in office I can visit?",
        a: "SwiftTest is a fully mobile service — we come to you. There is no public walk-in location, which keeps the service private, fast, and convenient.",
      },
    ],
  },
  {
    category: "Drug & DOT Testing",
    items: [
      {
        q: "What drug testing panels do you offer?",
        a: "We offer standard 5-panel and 10-panel urine tests, oral fluid (saliva) testing, rapid-result screening, and expanded panels on request, all backed by certified laboratories.",
      },
      {
        q: "Are your collectors DOT certified?",
        a: "Yes. DOT collections are performed by certified collectors following 49 CFR Part 40 procedures for FMCSA, FAA, and FRA regulated employees, including owner-operators.",
      },
      {
        q: "Can you handle post-accident testing at night or on weekends?",
        a: "Absolutely. Post-accident and reasonable-suspicion testing is available 24/7, including nights, weekends, and holidays, so you never miss a regulatory window.",
      },
      {
        q: "How quickly do results come back?",
        a: "Rapid screens can provide preliminary results on-site. Lab-confirmed results typically return within 24–72 hours depending on the panel, delivered securely to the authorized contact.",
      },
    ],
  },
  {
    category: "DNA & Legal Testing",
    items: [
      {
        q: "Is your DNA testing admissible in court?",
        a: "Yes. Legal DNA collections follow a strict documented chain of custody with identity verification and accredited laboratory processing, making results court-admissible.",
      },
      {
        q: "Where can a DNA collection take place?",
        a: "Anywhere private and convenient — your home, an attorney's office, or another agreed location. We handle everything discreetly.",
      },
    ],
  },
  {
    category: "Employer Accounts & Billing",
    items: [
      {
        q: "Can you set up an ongoing testing program for our company?",
        a: "Yes. We build custom programs for random testing pools, pre-employment screening, and post-accident protocols, with consolidated billing and a single point of contact.",
      },
      {
        q: "How does billing work?",
        a: "We invoice businesses directly for scheduled and program testing, and accept payment at time of service for individual appointments. Contact us for a straightforward quote.",
      },
    ],
  },
];

export const IMAGES = {
  hero: {
    url: "https://images.unsplash.com/photo-1631815590058-860e4f83c1e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MTJ8MHwxfHNlYXJjaHwxfHxtb2JpbGUlMjBoZWFsdGhjYXJlJTIwd29ya2VyJTIwdGVzdGluZyUyMG1lZGljYWwlMjBudXJzZSUyMHBobGVib3RvbWlzdHxlbnwwfHx8fDE3OTA2NzAzMzh8MA&ixlib=rb-4.1.0&q=85",
    alt: "Healthcare professional conducting a friendly on-site medical screening",
  },
  lab: {
    url: "https://images.unsplash.com/photo-1606206591513-adbfbdd7a177?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1RDR8MHwxfHNlYXJjaHszfHxtZWRpY2FsJTIwdGVzdCUyMHR1YmVzJTIwbGFib3JhdG9yeSUyMGNvbGxlY3Rpb24lMjBzYW1wbGUlMjBraXR8ZW58MHx8fHwxNzkwNjcwMzQ4fDA&ixlib=rb-4.1.0&q=85",
    alt: "Organized clinical testing collection tubes in a lab rack",
  },
  custody: {
    url: "https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1RDR8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwdGVzdCUyMHR1YmVzJTIwbGFib3JhdG9yeSUyMGNvbGxlY3Rpb24lMjBzYW1wbGUlMjBraXR8ZW58MHx8fHwxNzkwNjcwMzQ4fDA&ixlib=rb-4.1.0&q=85",
    alt: "Gloved specialist handling a certified medical specimen",
  },
  team: {
    url: "https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFsdGhjYXJlJTIwdGVhbSUyMGRpc2N1c3Npb24lMjB0YWJsZXQlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzkwNjcwMzQ4fDA&ixlib=rb-4.1.0&q=85",
    alt: "Healthcare specialists reviewing mobile testing protocols on a tablet",
  },
  mobile: {
    url: "https://images.unsplash.com/photo-1666886573531-48d2e3c2b684?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA7MDR8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsJTIwd29ya3BsYWNlJTIwdGFibGV0JTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc5MTEwNjA4OHww&ixlib=rb-4.1.0&q=85",
    alt: "Mobile healthcare professional reviewing workplace specimen testing paperwork with a client on a tablet",
  },
};
